<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';
import { usePermissions } from 'dashboard/composables/usePermissions';
import InsightsWidget from 'dashboard/components-next/captain/brain/InsightsWidget.vue';

const store = useStore();
const router = useRouter();
const { accountScopedRoute } = useAccount();
const { canEditPipeline } = usePermissions();

// ── Current User ──
const currentUser = useMapGetter('getCurrentUser');
const currentRole = useMapGetter('getCurrentRole');
const isAdmin = computed(
  () => currentRole.value === 'administrator'
);
const canSeeOverview = computed(
  () => isAdmin.value || canEditPipeline.value
);
const userName = computed(() => {
  const name = currentUser.value?.name || '';
  return name.split(' ')[0] || 'Usuario';
});

// ── View Toggle ──
const activeView = ref('admin');
const switchView = view => {
  activeView.value = view;
};

// Helper: safely convert string/number to float
const num = v => parseFloat(v) || 0;

// ── Date ──
const todayFormatted = computed(() => {
  const d = new Date();
  const weekdays = [
    'Domingo', 'Segunda-feira', 'Terca-feira',
    'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sabado',
  ];
  const months = [
    'janeiro', 'fevereiro', 'marco', 'abril', 'maio', 'junho',
    'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro',
  ];
  return `${weekdays[d.getDay()]}, ${d.getDate()} de ${months[d.getMonth()]}`;
});

const greeting = computed(() => {
  const h = new Date().getHours();
  if (h < 12) return 'Bom dia';
  if (h < 18) return 'Boa tarde';
  return 'Boa noite';
});

// ── Data Sources (server-side aggregated) ──
const homeStats = useMapGetter('dashboardHome/getHomeStats');
const myStats = useMapGetter('dashboardHome/getMyStats');
const companyStats = useMapGetter('dashboardHome/getCompanyStats');
const leaderboard = useMapGetter('dashboardHome/getLeaderboard');
const funnel = useMapGetter('dashboardHome/getFunnel');
const myRecentDeals = useMapGetter('dashboardHome/getMyRecentDeals');
const companyRecentDeals = useMapGetter('dashboardHome/getCompanyRecentDeals');
const myTasks = useMapGetter('dashboardHome/getMyTasks');
const myMeetings = useMapGetter('dashboardHome/getMyMeetings');
const homeLoading = useMapGetter('dashboardHome/getUIFlags');

// Lightweight data kept client-side (few records)
const agents = useMapGetter('agents/getAgents');
const conversationStats = useMapGetter('conversationStats/getStats');

// ── Load Data (3 calls instead of 7) ──
onMounted(() => {
  if (!canSeeOverview.value) activeView.value = 'agent';
  store.dispatch('dashboardHome/fetchHomeStats');
  store.dispatch('agents/get');
  store.dispatch('conversationStats/get');
});

// ── Computed from server data ──
const myConversationCount = computed(() => conversationStats.value?.mineCount || 0);
const totalConversations = computed(() => conversationStats.value?.allCount || 0);
const unassignedConversations = computed(() => conversationStats.value?.unAssignedCount || 0);

const onlineAgents = computed(() =>
  agents.value.filter(a => a.availability_status === 'online')
);
const busyAgents = computed(() =>
  agents.value.filter(a => a.availability_status === 'busy')
);
const offlineAgents = computed(() =>
  agents.value.filter(
    a => a.availability_status === 'offline' || !a.availability_status
  )
);

// Funnel data derived from server response
const funnelData = computed(() => {
  const stages = funnel.value;
  if (!stages.length) return [];
  const isAgentView = activeView.value === 'agent';
  const maxCount = Math.max(...stages.map(s => isAgentView ? s.my_count : s.all_count), 1);
  const colors = ['#A8A29E', '#2563eb', '#d97706', '#9B2C2C', '#16a34a'];
  return stages.map((s, i) => {
    const count = isAgentView ? s.my_count : s.all_count;
    const value = isAgentView ? s.my_value : s.all_value;
    return {
      label: s.name,
      count,
      value,
      pct: (count / maxCount) * 100,
      color: s.color || colors[i % colors.length],
    };
  });
});

// ── Helpers ──
const formatCurrency = raw => {
  const val = num(raw);
  if (val >= 1000000) return `R$ ${(val / 1000000).toFixed(1)}M`;
  if (val >= 1000) return `R$ ${(val / 1000).toFixed(0)}k`;
  return `R$ ${val.toFixed(0)}`;
};

const initials = name => {
  if (!name) return '?';
  return name
    .split(' ')
    .map(w => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
};

const stageClass = status => {
  const map = {
    won: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400',
    lost: 'bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-400',
    churned: 'bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-400',
  };
  return map[status] || 'bg-n-alpha-2 text-n-slate-11';
};

const taskTimeLabel = task => {
  if (!task.due_date) return '';
  const due = new Date(task.due_date);
  const now = new Date();
  const diffMs = due - now;
  if (diffMs < 0) {
    const hours = Math.abs(Math.round(diffMs / 3600000));
    if (hours < 24) return `Atrasada ${hours}h`;
    return `Atrasada ${Math.round(hours / 24)}d`;
  }
  return due.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
};

const isOverdue = task => {
  return !!task.overdue;
};

const meetingTimeLabel = meeting => {
  if (!meeting.starts_at) return '';
  return new Date(meeting.starts_at).toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

// ── Today's Activities (tasks + meetings) ──
const activityFilter = ref('all'); // 'all' | 'tasks' | 'meetings'
const activityFilters = [
  { key: 'all', label: 'Todas' },
  { key: 'tasks', label: 'Tarefas' },
  { key: 'meetings', label: 'Reunioes' },
];

const activityItems = computed(() => {
  const tasks = myTasks.value.map(t => ({
    key: `task-${t.id}`,
    type: 'task',
    title: t.title,
    subtitle: [t.taskable_type === 'Deal' ? 'Deal' : '', t.description || '']
      .filter(Boolean)
      .join(' '),
    timeLabel: taskTimeLabel(t),
    overdue: isOverdue(t),
    sortAt: t.due_date ? new Date(t.due_date).getTime() : Infinity,
  }));
  const meetings = myMeetings.value.map(m => ({
    key: `meeting-${m.id}`,
    type: 'meeting',
    title: m.title,
    subtitle: 'Reuniao',
    meetLink: m.meet_link,
    timeLabel: meetingTimeLabel(m),
    overdue: false,
    sortAt: m.starts_at ? new Date(m.starts_at).getTime() : Infinity,
  }));
  if (activityFilter.value === 'tasks') return tasks;
  if (activityFilter.value === 'meetings') return meetings;
  return [...tasks, ...meetings].sort((a, b) => a.sortAt - b.sortAt);
});

const emptyActivityLabel = computed(() => {
  if (activityFilter.value === 'meetings') return 'Nenhuma reuniao para hoje';
  if (activityFilter.value === 'tasks') return 'Nenhuma tarefa para hoje';
  return 'Nenhuma atividade para hoje';
});

const navigateTo = routeName => {
  router.push(accountScopedRoute(routeName));
};

const rankClass = idx => {
  if (idx === 0) return 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300';
  if (idx === 1) return 'bg-n-alpha-2 text-n-slate-11';
  if (idx === 2) return 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300';
  return 'bg-n-alpha-1 text-n-slate-10';
};

const avatarColors = [
  'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
  'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400',
  'bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400',
  'bg-rose-50 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400',
  'bg-violet-50 text-violet-600 dark:bg-violet-900/30 dark:text-violet-400',
];

const avatarColor = (name, idx) => avatarColors[(idx || 0) % avatarColors.length];

const statusDot = status => {
  if (status === 'online') return 'bg-emerald-500';
  if (status === 'busy') return 'bg-amber-500';
  return 'bg-n-slate-8';
};

const isLoading = computed(() => homeLoading.value?.isFetching);
</script>

<template>
  <div class="w-full overflow-y-auto bg-n-surface-1">
    <div class="max-w-[72rem] mx-auto px-4 py-5 sm:px-6 sm:py-6">
      <!-- Greeting + View Toggle -->
      <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-5 sm:mb-6">
        <div>
          <h2
            class="text-lg sm:text-[22px] font-semibold text-n-slate-12"
            style="font-family: 'Space Grotesk', var(--font-inter), sans-serif"
          >
            {{ greeting }}, {{ userName }}
          </h2>
          <p class="text-xs sm:text-sm text-n-slate-10 mt-0.5">{{ todayFormatted }}</p>
        </div>
        <div
          v-if="canSeeOverview"
          class="flex bg-n-alpha-2 rounded-lg p-0.5 gap-0.5 self-start"
        >
          <button
            class="px-3 py-1.5 text-xs font-medium rounded-md transition-all"
            :class="
              activeView === 'agent'
                ? 'bg-n-solid-2 text-n-slate-12 shadow-sm outline outline-1 outline-n-container'
                : 'text-n-slate-10 hover:text-n-slate-12'
            "
            @click="switchView('agent')"
          >
            Meu Painel
          </button>
          <button
            class="px-3 py-1.5 text-xs font-medium rounded-md transition-all"
            :class="
              activeView === 'admin'
                ? 'bg-n-solid-2 text-n-slate-12 shadow-sm outline outline-1 outline-n-container'
                : 'text-n-slate-10 hover:text-n-slate-12'
            "
            @click="switchView('admin')"
          >
            Visão Geral
          </button>
        </div>
      </div>

      <!-- Captain Insights Widget -->
      <InsightsWidget :mode="activeView" class="mb-5" />

      <!-- Loading skeleton (only on first load) -->
      <div v-if="isLoading && !homeStats.my_stats" class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-5">
        <div
          v-for="i in 5"
          :key="i"
          class="h-24 rounded-xl bg-n-alpha-2 animate-pulse"
        />
      </div>

      <!-- ═══════════════════════════════ -->
      <!-- AGENT VIEW                      -->
      <!-- ═══════════════════════════════ -->
      <template v-if="activeView === 'agent'">
        <!-- KPI Cards -->
        <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-5">
          <div
            class="rounded-xl bg-n-solid-2 outline outline-1 outline-n-container p-4"
          >
            <div class="text-[11px] font-medium text-n-slate-10 uppercase tracking-wide mb-2">
              Meus Deals
            </div>
            <div
              class="text-xl sm:text-[26px] font-bold text-n-slate-12 leading-none"
              style="font-family: 'Space Grotesk', sans-serif"
            >
              {{ myStats.active_deals || 0 }}
            </div>
          </div>
          <div
            class="rounded-xl bg-n-solid-2 outline outline-1 outline-n-container p-4"
          >
            <div class="text-[11px] font-medium text-n-slate-10 uppercase tracking-wide mb-2">
              Meu Pipeline
            </div>
            <div
              class="text-xl sm:text-[26px] font-bold text-n-slate-12 leading-none"
              style="font-family: 'Space Grotesk', sans-serif"
            >
              {{ formatCurrency(myStats.pipeline_value || 0) }}
            </div>
          </div>
          <div
            class="rounded-xl bg-n-solid-2 outline outline-1 outline-n-container p-4"
          >
            <div class="text-[11px] font-medium text-n-slate-10 uppercase tracking-wide mb-2">
              Ganhos este Mes
            </div>
            <div
              class="text-xl sm:text-[26px] font-bold text-n-slate-12 leading-none"
              style="font-family: 'Space Grotesk', sans-serif"
            >
              {{ formatCurrency(myStats.won_value || 0) }}
            </div>
            <div class="text-[11px] text-n-slate-10 mt-1.5">
              {{ myStats.won_count || 0 }} deals fechados
            </div>
          </div>
          <div
            class="rounded-xl bg-n-solid-2 outline outline-1 outline-n-container p-4 relative"
          >
            <div class="text-[11px] font-medium text-n-slate-10 uppercase tracking-wide mb-2">
              Minhas Conversas
            </div>
            <div
              class="text-xl sm:text-[26px] font-bold text-n-slate-12 leading-none"
              style="font-family: 'Space Grotesk', sans-serif"
            >
              {{ myConversationCount }}
            </div>
          </div>
          <div
            class="rounded-xl bg-n-solid-2 outline outline-1 outline-n-container p-4"
          >
            <div class="text-[11px] font-medium text-n-slate-10 uppercase tracking-wide mb-2">
              Tarefas Pendentes
            </div>
            <div
              class="text-xl sm:text-[26px] font-bold text-n-slate-12 leading-none"
              style="font-family: 'Space Grotesk', sans-serif"
            >
              {{ myStats.pending_tasks || 0 }}
            </div>
            <div
              v-if="(myStats.overdue_tasks || 0) > 0"
              class="text-[11px] text-red-500 font-medium mt-1.5"
            >
              {{ myStats.overdue_tasks }} atrasadas
            </div>
          </div>
        </div>

        <!-- Pipeline + Activities -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
          <!-- Pipeline Funnel -->
          <div
            class="rounded-xl bg-n-solid-2 outline outline-1 outline-n-container overflow-hidden"
          >
            <div
              class="flex items-center justify-between px-4 pt-3.5 pb-2.5 border-b border-n-weak"
            >
              <span class="text-[13px] font-semibold text-n-slate-12">
                Meu Pipeline
              </span>
              <button
                class="text-[11px] font-medium text-n-brand hover:underline"
                @click="navigateTo('crm_board')"
              >
                Ver board
              </button>
            </div>
            <div class="p-4 space-y-2.5">
              <template v-if="funnelData.length">
                <div
                  v-for="stage in funnelData"
                  :key="stage.label"
                  class="flex items-center gap-3"
                >
                  <span
                    class="w-16 sm:w-24 text-[11px] sm:text-xs text-n-slate-11 text-right flex-shrink-0 truncate"
                  >
                    {{ stage.label }}
                  </span>
                  <div
                    class="flex-1 h-7 bg-n-alpha-1 rounded-md overflow-hidden"
                  >
                    <div
                      class="h-full rounded-md flex items-center pl-2.5 text-[11px] font-semibold text-white"
                      :style="{
                        width: `${Math.max(stage.pct, 8)}%`,
                        backgroundColor: stage.color,
                      }"
                    >
                      {{ stage.count }}
                    </div>
                  </div>
                  <span
                    class="hidden sm:inline w-20 text-[11px] text-n-slate-10 text-right flex-shrink-0"
                  >
                    {{ formatCurrency(stage.value) }}
                  </span>
                </div>
              </template>
              <div
                v-else
                class="py-8 text-center text-sm text-n-slate-10"
              >
                Nenhum pipeline ativo
              </div>
            </div>
          </div>

          <!-- Activities / Tasks -->
          <div
            class="rounded-xl bg-n-solid-2 outline outline-1 outline-n-container overflow-hidden"
          >
            <div
              class="flex items-center justify-between px-4 pt-3.5 pb-2.5 border-b border-n-weak"
            >
              <span class="text-[13px] font-semibold text-n-slate-12 flex items-center gap-2">
                Atividades de Hoje
                <span
                  v-if="activityItems.length"
                  class="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-n-brand/10 text-n-brand"
                >
                  {{ activityItems.length }}
                </span>
              </span>
              <button
                class="text-[11px] font-medium text-n-brand hover:underline"
                @click="navigateTo('crm_tasks')"
              >
                Ver todas
              </button>
            </div>
            <!-- Sub-filter: Todas / Tarefas / Reunioes -->
            <div class="flex items-center gap-1 px-4 pt-2.5 pb-1">
              <button
                v-for="f in activityFilters"
                :key="f.key"
                class="px-2.5 py-1 text-[11px] font-medium rounded-md transition-colors"
                :class="
                  activityFilter === f.key
                    ? 'bg-n-brand/10 text-n-brand'
                    : 'text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-1'
                "
                @click="activityFilter = f.key"
              >
                {{ f.label }}
              </button>
            </div>
            <div>
              <template v-if="activityItems.length">
                <div
                  v-for="item in activityItems"
                  :key="item.key"
                  class="flex items-start gap-3 px-4 py-3 border-b border-n-weak/50 last:border-b-0 hover:bg-n-alpha-1 transition-colors cursor-pointer"
                >
                  <div
                    class="w-8 h-8 rounded-lg grid place-content-center flex-shrink-0 text-sm"
                    :class="
                      item.overdue
                        ? 'bg-red-50 text-red-500 dark:bg-red-900/30 dark:text-red-400'
                        : item.type === 'meeting'
                          ? 'bg-n-teal-9/10 text-n-teal-9'
                          : 'bg-n-brand/10 text-n-brand'
                    "
                  >
                    <span
                      :class="[
                        item.type === 'meeting'
                          ? 'i-lucide-calendar-clock'
                          : item.overdue
                            ? 'i-lucide-alert-circle'
                            : 'i-lucide-check-circle',
                        'size-4',
                      ]"
                    />
                  </div>
                  <div class="flex-1 min-w-0">
                    <div
                      class="text-[13px] font-medium text-n-slate-12 truncate"
                    >
                      {{ item.title }}
                    </div>
                    <div
                      class="text-[11px] text-n-slate-10 mt-0.5 flex items-center gap-1.5"
                    >
                      <span class="truncate">{{ item.subtitle }}</span>
                      <a
                        v-if="item.meetLink"
                        :href="item.meetLink"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="text-n-brand hover:underline shrink-0"
                        @click.stop
                      >
                        Meet
                      </a>
                    </div>
                  </div>
                  <span
                    class="text-[11px] flex-shrink-0 whitespace-nowrap"
                    :class="
                      item.overdue
                        ? 'text-red-500 font-semibold'
                        : 'text-n-slate-10'
                    "
                  >
                    {{ item.timeLabel }}
                  </span>
                </div>
              </template>
              <div
                v-else
                class="py-10 text-center text-sm text-n-slate-10"
              >
                {{ emptyActivityLabel }}
              </div>
            </div>
          </div>
        </div>

        <!-- Recent Deals (full width for agent view) -->
        <div class="grid grid-cols-1 gap-4 mb-5">
          <div
            class="rounded-xl bg-n-solid-2 outline outline-1 outline-n-container overflow-hidden"
          >
            <div
              class="flex items-center justify-between px-4 pt-3.5 pb-2.5 border-b border-n-weak"
            >
              <span class="text-[13px] font-semibold text-n-slate-12">
                Meus Deals Recentes
              </span>
              <button
                class="text-[11px] font-medium text-n-brand hover:underline"
                @click="navigateTo('crm_board')"
              >
                Ver todos
              </button>
            </div>
            <div>
              <template v-if="myRecentDeals.length">
                <div
                  v-for="deal in myRecentDeals"
                  :key="deal.id"
                  class="flex items-center gap-3 px-4 py-3 border-b border-n-weak/50 last:border-b-0 hover:bg-n-alpha-1 transition-colors cursor-pointer"
                >
                  <div
                    class="w-9 h-9 rounded-lg bg-brand-gradient grid place-content-center text-white font-bold text-[13px] flex-shrink-0"
                  >
                    {{ initials(deal.title) }}
                  </div>
                  <div class="flex-1 min-w-0">
                    <div
                      class="text-[13px] font-medium text-n-slate-12 truncate"
                    >
                      {{ deal.title }}
                    </div>
                    <div class="text-[11px] text-n-slate-10 mt-0.5 truncate">
                      {{ deal.company || '' }}
                    </div>
                  </div>
                  <span
                    class="text-[10px] font-semibold px-2 py-1 rounded-md flex-shrink-0"
                    :class="stageClass(deal.status)"
                  >
                    {{ deal.stage || deal.status }}
                  </span>
                  <span
                    class="text-sm font-semibold text-n-slate-12 flex-shrink-0"
                    style="font-family: 'Space Grotesk', sans-serif"
                  >
                    {{ formatCurrency(deal.value || 0) }}
                  </span>
                </div>
              </template>
              <div
                v-else
                class="py-10 text-center text-sm text-n-slate-10"
              >
                Nenhum deal encontrado
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- ═══════════════════════════════ -->
      <!-- ADMIN VIEW                      -->
      <!-- ═══════════════════════════════ -->
      <template v-else-if="activeView === 'admin'">
        <!-- KPI Cards -->
        <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-5">
          <div
            class="rounded-xl bg-n-solid-2 outline outline-1 outline-n-container p-4"
          >
            <div class="text-[11px] font-medium text-n-slate-10 uppercase tracking-wide mb-2">
              Total Deals
            </div>
            <div
              class="text-xl sm:text-[26px] font-bold text-n-slate-12 leading-none"
              style="font-family: 'Space Grotesk', sans-serif"
            >
              {{ companyStats.total_deals || 0 }}
            </div>
          </div>
          <div
            class="rounded-xl bg-n-solid-2 outline outline-1 outline-n-container p-4"
          >
            <div class="text-[11px] font-medium text-n-slate-10 uppercase tracking-wide mb-2">
              Pipeline Total
            </div>
            <div
              class="text-xl sm:text-[26px] font-bold text-n-slate-12 leading-none"
              style="font-family: 'Space Grotesk', sans-serif"
            >
              {{ formatCurrency(companyStats.pipeline_value || 0) }}
            </div>
          </div>
          <div
            class="rounded-xl bg-n-solid-2 outline outline-1 outline-n-container p-4"
          >
            <div class="text-[11px] font-medium text-n-slate-10 uppercase tracking-wide mb-2">
              Receita do Mes
            </div>
            <div
              class="text-xl sm:text-[26px] font-bold text-n-slate-12 leading-none"
              style="font-family: 'Space Grotesk', sans-serif"
            >
              {{ formatCurrency(companyStats.month_won_value || 0) }}
            </div>
            <div class="text-[11px] text-n-slate-10 mt-1.5">
              {{ companyStats.month_won_count || 0 }} deals fechados
            </div>
          </div>
          <div
            class="rounded-xl bg-n-solid-2 outline outline-1 outline-n-container p-4 relative"
          >
            <div class="text-[11px] font-medium text-n-slate-10 uppercase tracking-wide mb-2">
              Conversas Totais
            </div>
            <div
              class="text-xl sm:text-[26px] font-bold text-n-slate-12 leading-none"
              style="font-family: 'Space Grotesk', sans-serif"
            >
              {{ totalConversations }}
            </div>
            <div
              v-if="unassignedConversations > 0"
              class="text-[11px] text-amber-500 font-medium mt-1.5"
            >
              {{ unassignedConversations }} não atribuídas
            </div>
          </div>
          <div
            class="rounded-xl bg-n-solid-2 outline outline-1 outline-n-container p-4"
          >
            <div class="text-[11px] font-medium text-n-slate-10 uppercase tracking-wide mb-2">
              Agentes Ativos
            </div>
            <div
              class="text-xl sm:text-[26px] font-bold text-n-slate-12 leading-none"
              style="font-family: 'Space Grotesk', sans-serif"
            >
              {{ onlineAgents.length + busyAgents.length }}
            </div>
            <div class="text-[11px] text-n-slate-10 mt-1.5">
              de {{ agents.length }} no total
            </div>
          </div>
        </div>

        <!-- Pipeline + Team Status -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
          <!-- Pipeline Funnel (company-wide) -->
          <div
            class="rounded-xl bg-n-solid-2 outline outline-1 outline-n-container overflow-hidden"
          >
            <div
              class="flex items-center justify-between px-4 pt-3.5 pb-2.5 border-b border-n-weak"
            >
              <span class="text-[13px] font-semibold text-n-slate-12">
                Pipeline da Empresa
              </span>
              <button
                class="text-[11px] font-medium text-n-brand hover:underline"
                @click="navigateTo('crm_board')"
              >
                Ver board
              </button>
            </div>
            <div class="p-4 space-y-2.5">
              <template v-if="funnelData.length">
                <div
                  v-for="stage in funnelData"
                  :key="stage.label"
                  class="flex items-center gap-3"
                >
                  <span
                    class="w-16 sm:w-24 text-[11px] sm:text-xs text-n-slate-11 text-right flex-shrink-0 truncate"
                  >
                    {{ stage.label }}
                  </span>
                  <div
                    class="flex-1 h-7 bg-n-alpha-1 rounded-md overflow-hidden"
                  >
                    <div
                      class="h-full rounded-md flex items-center pl-2.5 text-[11px] font-semibold text-white"
                      :style="{
                        width: `${Math.max(stage.pct, 8)}%`,
                        backgroundColor: stage.color,
                      }"
                    >
                      {{ stage.count }}
                    </div>
                  </div>
                  <span
                    class="hidden sm:inline w-20 text-[11px] text-n-slate-10 text-right flex-shrink-0"
                  >
                    {{ formatCurrency(stage.value) }}
                  </span>
                </div>
              </template>
              <div
                v-else
                class="py-8 text-center text-sm text-n-slate-10"
              >
                Nenhum pipeline ativo
              </div>
            </div>
          </div>

          <!-- Team Status -->
          <div
            class="rounded-xl bg-n-solid-2 outline outline-1 outline-n-container overflow-hidden"
          >
            <div
              class="flex items-center justify-between px-4 pt-3.5 pb-2.5 border-b border-n-weak"
            >
              <span class="text-[13px] font-semibold text-n-slate-12">
                Status do Time
              </span>
            </div>
            <!-- Status Summary -->
            <div class="grid grid-cols-3 gap-2 sm:gap-3 p-3 sm:p-4">
              <div
                class="text-center py-3 px-2 rounded-lg bg-n-alpha-1 outline outline-1 outline-n-weak/50"
              >
                <div
                  class="text-2xl font-bold leading-none text-emerald-500"
                  style="font-family: 'Space Grotesk', sans-serif"
                >
                  {{ onlineAgents.length }}
                </div>
                <div class="text-[11px] text-n-slate-10 mt-1">Disponiveis</div>
              </div>
              <div
                class="text-center py-3 px-2 rounded-lg bg-n-alpha-1 outline outline-1 outline-n-weak/50"
              >
                <div
                  class="text-2xl font-bold leading-none text-amber-500"
                  style="font-family: 'Space Grotesk', sans-serif"
                >
                  {{ busyAgents.length }}
                </div>
                <div class="text-[11px] text-n-slate-10 mt-1">Ocupados</div>
              </div>
              <div
                class="text-center py-3 px-2 rounded-lg bg-n-alpha-1 outline outline-1 outline-n-weak/50"
              >
                <div
                  class="text-2xl font-bold leading-none text-n-slate-9"
                  style="font-family: 'Space Grotesk', sans-serif"
                >
                  {{ offlineAgents.length }}
                </div>
                <div class="text-[11px] text-n-slate-10 mt-1">Offline</div>
              </div>
            </div>
            <!-- Agent list -->
            <div>
              <div
                v-for="(agent, idx) in [
                  ...onlineAgents,
                  ...busyAgents,
                ].slice(0, 5)"
                :key="agent.id"
                class="flex items-center gap-3 px-4 py-2.5 border-t border-n-weak/50"
              >
                <div
                  class="w-2 h-2 rounded-full flex-shrink-0"
                  :class="statusDot(agent.availability_status)"
                />
                <div
                  class="w-8 h-8 rounded-full grid place-content-center text-xs font-semibold flex-shrink-0"
                  :class="avatarColor(agent.name, idx)"
                >
                  {{ initials(agent.name) }}
                </div>
                <div class="flex-1 min-w-0">
                  <div class="text-[13px] font-medium text-n-slate-12">
                    {{ agent.name }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Leaderboard + Recent Deals -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
          <!-- Leaderboard -->
          <div
            class="rounded-xl bg-n-solid-2 outline outline-1 outline-n-container overflow-hidden"
          >
            <div
              class="flex items-center justify-between px-4 pt-3.5 pb-2.5 border-b border-n-weak"
            >
              <span class="text-[13px] font-semibold text-n-slate-12">
                Ranking do Mes
              </span>
              <button
                class="text-[11px] font-medium text-n-brand hover:underline"
                @click="navigateTo('crm_reports')"
              >
                Ver relatorio
              </button>
            </div>
            <div>
              <template v-if="leaderboard.length">
                <div
                  v-for="(agent, idx) in leaderboard"
                  :key="agent.user_id"
                  class="flex items-center gap-3 px-4 py-2.5 border-b border-n-weak/50 last:border-b-0"
                >
                  <div
                    class="w-5.5 h-5.5 rounded-md grid place-content-center text-[11px] font-bold flex-shrink-0"
                    :class="rankClass(idx)"
                  >
                    {{ idx + 1 }}
                  </div>
                  <div
                    class="w-8 h-8 rounded-full grid place-content-center text-xs font-semibold flex-shrink-0"
                    :class="avatarColor(agent.name, idx)"
                  >
                    {{ initials(agent.name) }}
                  </div>
                  <div class="flex-1 min-w-0">
                    <div class="text-[13px] font-medium text-n-slate-12">
                      {{ agent.name }}
                    </div>
                    <div class="text-[11px] text-n-slate-10 mt-0.5">
                      {{ agent.won_deals }} deals ganhos
                    </div>
                  </div>
                  <span
                    class="text-sm font-bold text-n-slate-12 flex-shrink-0"
                    style="font-family: 'Space Grotesk', sans-serif"
                  >
                    {{ formatCurrency(agent.won_value) }}
                  </span>
                </div>
              </template>
              <div
                v-else
                class="py-10 text-center text-sm text-n-slate-10"
              >
                Nenhum dado disponível
              </div>
            </div>
          </div>

          <!-- Company Recent Deals -->
          <div
            class="rounded-xl bg-n-solid-2 outline outline-1 outline-n-container overflow-hidden"
          >
            <div
              class="flex items-center justify-between px-4 pt-3.5 pb-2.5 border-b border-n-weak"
            >
              <span class="text-[13px] font-semibold text-n-slate-12">
                Deals Recentes
              </span>
              <button
                class="text-[11px] font-medium text-n-brand hover:underline"
                @click="navigateTo('crm_board')"
              >
                Ver todos
              </button>
            </div>
            <div>
              <template v-if="companyRecentDeals.length">
                <div
                  v-for="deal in companyRecentDeals"
                  :key="deal.id"
                  class="flex items-center gap-3 px-4 py-3 border-b border-n-weak/50 last:border-b-0 hover:bg-n-alpha-1 transition-colors cursor-pointer"
                >
                  <div
                    class="w-9 h-9 rounded-lg bg-brand-gradient grid place-content-center text-white font-bold text-[13px] flex-shrink-0"
                  >
                    {{ initials(deal.title) }}
                  </div>
                  <div class="flex-1 min-w-0">
                    <div
                      class="text-[13px] font-medium text-n-slate-12 truncate"
                    >
                      {{ deal.title }}
                    </div>
                    <div class="text-[11px] text-n-slate-10 mt-0.5 truncate">
                      {{ deal.company || '' }}
                      <template v-if="deal.owner">
                        · Resp: {{ deal.owner }}
                      </template>
                    </div>
                  </div>
                  <span
                    class="text-[10px] font-semibold px-2 py-1 rounded-md flex-shrink-0"
                    :class="stageClass(deal.status)"
                  >
                    {{ deal.stage || deal.status }}
                  </span>
                  <span
                    class="text-sm font-semibold text-n-slate-12 flex-shrink-0"
                    style="font-family: 'Space Grotesk', sans-serif"
                  >
                    {{ formatCurrency(deal.value || 0) }}
                  </span>
                </div>
              </template>
              <div
                v-else
                class="py-10 text-center text-sm text-n-slate-10"
              >
                Nenhum deal encontrado
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>
